/**
 * NFC Bridge Integration Service
 * 
 * Provides interface to VAIU NFC Bridge native app
 * Handles card reading and communication with bridge app
 */

export interface TapToPayNativeResult {
  success: boolean;
  error?: string;
}

/** Result of reading PoS ingress (NDEF Text/URI → raw string for `parseVaiuPosIngress`). */
export interface PoSPayloadReadResult {
  success: boolean;
  payload?: string;
  error?: string;
}

export interface PoSWriteResult {
  success: boolean;
  error?: string;
}

export interface VaiuNFCBridge {
  isAvailable(): Promise<boolean>;
  readCard(options: {
    amount: number;
    currency: string;
  }): Promise<{
    success: boolean;
    cardData?: {
      pan: string;
      lastFour: string;
      expiryMonth: number;
      expiryYear: number;
      cardholderName: string;
      cardBrand: string;
      cvv?: string;
      encryptedData?: string;
    };
    error?: string;
  }>;
  getStatus(): Promise<{
    available: boolean;
    enabled: boolean;
    platform: 'android' | 'ios' | 'unknown';
  }>;
  /**
   * Outbound PoS tap: native shell fetches credentials via tapSessionId (see TAP-SESSION-API.md).
   */
  startTapToPay(options: {
    tapSessionId: string;
    accessToken: string;
  }): Promise<TapToPayNativeResult>;

  /** PoS NDEF ingress (see VAIU-POS-EXCHANGE.md). */
  readPoSPayload?(): Promise<PoSPayloadReadResult>;

  /** Write cash token key back to PoS tag (NDEF). */
  writePoSResponse?(tokenKey: string, format?: 'text' | 'uri'): Promise<PoSWriteResult>;
}

declare global {
  interface Window {
    VaiuNFC?: VaiuNFCBridge;
  }
}

/**
 * NFC Bridge Service
 * Wraps window.VaiuNFC with error handling and fallbacks
 */
export class NFCBridgeService {
  /**
   * Check if NFC bridge is available
   */
  async isAvailable(): Promise<boolean> {
    if (typeof window === 'undefined') {
      return false;
    }

    const vaiuNFC = window.VaiuNFC;
    if (!vaiuNFC) {
      return false;
    }

    try {
      return await vaiuNFC.isAvailable();
    } catch (error) {
      console.error('[NFC Bridge] Error checking availability:', error);
      return false;
    }
  }

  /**
   * Read card via NFC
   */
  async readCard(options: {
    amount: number;
    currency: string;
  }): Promise<{
    success: boolean;
    cardData?: {
      pan: string;
      lastFour: string;
      expiryMonth: number;
      expiryYear: number;
      cardholderName: string;
      cardBrand: string;
      cvv?: string;
      encryptedData?: string;
    };
    error?: string;
  }> {
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Not available in server environment',
      };
    }

    const vaiuNFC = window.VaiuNFC;
    if (!vaiuNFC) {
      return {
        success: false,
        error: 'NFC Bridge app not installed. Please install the VAIU NFC Bridge app.',
      };
    }

    try {
      return await vaiuNFC.readCard(options);
    } catch (error: any) {
      console.error('[NFC Bridge] Error reading card:', error);
      return {
        success: false,
        error: error.message || 'Failed to read card',
      };
    }
  }

  /**
   * Start native tap-to-pay flow (NFC shell). Never pass PAN — only tapSessionId + auth.
   */
  /**
   * Read PoS checkout payload from NDEF (NFC shell). Returns raw string (JSON or URI).
   */
  async readPoSPayload(): Promise<PoSPayloadReadResult> {
    if (typeof window === 'undefined') {
      return { success: false, error: 'Not available in server environment' };
    }
    const vaiuNFC = window.VaiuNFC;
    if (!vaiuNFC?.readPoSPayload) {
      return { success: false, error: 'PoS NFC read not supported in this shell. Update the VAIU NFC app.' };
    }
    try {
      return await vaiuNFC.readPoSPayload();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'readPoSPayload failed';
      console.error('[NFC Bridge] readPoSPayload:', error);
      return { success: false, error: msg };
    }
  }

  /**
   * Write token key to PoS via NDEF (basket return leg).
   */
  async writePoSResponse(
    tokenKey: string,
    format: 'text' | 'uri' = 'text',
  ): Promise<PoSWriteResult> {
    if (typeof window === 'undefined') {
      return { success: false, error: 'Not available in server environment' };
    }
    const vaiuNFC = window.VaiuNFC;
    if (!vaiuNFC?.writePoSResponse) {
      return { success: false, error: 'PoS NFC write not supported in this shell. Update the VAIU NFC app.' };
    }
    try {
      return await vaiuNFC.writePoSResponse(tokenKey, format);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'writePoSResponse failed';
      console.error('[NFC Bridge] writePoSResponse:', error);
      return { success: false, error: msg };
    }
  }

  async startTapToPay(options: {
    tapSessionId: string;
    accessToken: string;
  }): Promise<TapToPayNativeResult> {
    if (typeof window === 'undefined') {
      return { success: false, error: 'Not available in server environment' };
    }

    const vaiuNFC = window.VaiuNFC;
    if (!vaiuNFC?.startTapToPay) {
      return {
        success: false,
        error: 'NFC Bridge does not support tap to pay. Update the VAIU NFC shell app.',
      };
    }

    try {
      return await vaiuNFC.startTapToPay({
        tapSessionId: options.tapSessionId,
        accessToken: options.accessToken,
      });
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'startTapToPay failed';
      console.error('[NFC Bridge] startTapToPay:', error);
      return { success: false, error: msg };
    }
  }

  /**
   * Get NFC bridge status
   */
  async getStatus(): Promise<{
    available: boolean;
    enabled: boolean;
    platform: 'android' | 'ios' | 'unknown';
  }> {
    if (typeof window === 'undefined') {
      return {
        available: false,
        enabled: false,
        platform: 'unknown',
      };
    }

    const vaiuNFC = window.VaiuNFC;
    if (!vaiuNFC) {
      return {
        available: false,
        enabled: false,
        platform: 'unknown',
      };
    }

    try {
      return await vaiuNFC.getStatus();
    } catch (error) {
      console.error('[NFC Bridge] Error getting status:', error);
      return {
        available: false,
        enabled: false,
        platform: 'unknown',
      };
    }
  }

  /**
   * Check if running on mobile device
   */
  isMobileDevice(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  /**
   * Get download link for NFC Bridge app
   */
  getDownloadLink(): { android?: string; ios?: string } {
    // These would be actual app store links
    return {
      android: 'https://play.google.com/store/apps/details?id=com.vaiu.nfcbridge',
      ios: 'https://apps.apple.com/app/vaiu-nfc-bridge/id123456789',
    };
  }
}

// Export singleton instance
export const nfcBridgeService = new NFCBridgeService();

